import {useMemo} from 'react';
import styled from 'styled-components';
import {graphql, useFragment} from 'react-relay';
import {Button, Detail, EMPHASIS, Label, SubHeading, Table, Text} from '@forecast-it/design-system';
import type {RelatedTab_ticket$key} from './__generated__/RelatedTab_ticket.graphql';
import {EmptyState} from './EmptyState';
import {STATUS_LABELS} from './TicketStatusBadge';
import {personDisplayName} from '../../../graphql/fragments/ticketingPerson';
import {useNavigate} from '../../../router/compat';
import {useDeleteTicketLink} from '../hooks/useDeleteTicketLink';
import {useAuth} from '../../auth/context/AuthContext';
import {hasTicketPerm, TICKET_PERMS} from '../../auth/utils/ticketPerms';
import type {TicketStatus} from '../types/ticket';

const RelatedTabFragment = graphql`
	fragment RelatedTab_ticket on Ticket {
		id
		links {
			id
			linkType
			createdAt
			targetTicket {
				id
				number
				subject
				status
				# Read inline as well as spread: the design system's avatar column takes a
				# plain string, and a bare fragment spread is only a ref — reading a name
				# off it yields nothing.
				assignee {
					fullName
					active
					...ticketingPerson_person
				}
			}
			targetProject {
				id
				name
				color
			}
		}
	}
`;

interface Props {
	ticket: RelatedTab_ticket$key;
	/** Named in the heading, so the reader knows whose other work this is. */
	requesterName?: string;
	onAddLink?: () => void;
	onRefetch?: () => void;
}

/** How a ticket-to-ticket link reads. `project` and `forecast_task` are not tickets. */
const RELATIONSHIP_LABELS: Record<string, string> = {
	related: 'Related',
	child: 'Child',
};

const Stack = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${p => p.theme.sys.spacing[500]};
`;
const Header = styled.div`
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: ${p => p.theme.sys.spacing[400]};
`;
const Intro = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${p => p.theme.sys.spacing[100]};
`;
const Group = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${p => p.theme.sys.spacing[200]};
`;
const GroupHead = styled.div`
	display: flex;
	align-items: center;
	gap: ${p => p.theme.sys.spacing[200]};
`;
const EmptyBody = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: ${p => p.theme.sys.spacing[200]};
`;

interface TicketLinkRow {
	id: string;
	slug: string;
	subject: string;
	relationship: string;
	status: string;
	assignee: string;
	targetId: string;
}

interface ProjectLinkRow {
	id: string;
	projectId: string;
	name: string;
	targetId: string;
}

/**
 * Everything else tied to this requester: their other tickets, and the projects the
 * work sits in.
 *
 * Grouped by what the thing IS — tickets, then projects — rather than by how it was
 * linked, because that is how a reader looks for it. The link's own kind survives as
 * the Relationship column, so a child ticket is still distinguishable from a merely
 * related one.
 *
 * The project rows carry only an id and a name: `ProjectLinkTarget` exposes id, name
 * and colour and nothing else, so there is no status or owner to show. A column of
 * em-dashes would be worse than no column.
 */
export function RelatedTab({ticket: ticketRef, requesterName, onAddLink, onRefetch}: Props) {
	const data = useFragment(RelatedTabFragment, ticketRef);
	const navigate = useNavigate();
	const {user} = useAuth();
	const {submit: deleteLink, loading: deleting} = useDeleteTicketLink();
	const canManage = !!user && hasTicketPerm(user.ticketPerms, TICKET_PERMS.MANAGE);

	const handleDelete = (linkId: string) => {
		deleteLink(linkId).then(
			() => onRefetch?.(),
			() => {}
		);
	};

	const ticketRows: TicketLinkRow[] = useMemo(
		() =>
			data.links
				.filter(link => link.targetTicket != null)
				.map(link => ({
					id: link.id,
					slug: `TK-${link.targetTicket?.number}`,
					subject: link.targetTicket?.subject ?? '—',
					relationship: RELATIONSHIP_LABELS[link.linkType] ?? 'Related',
					status: STATUS_LABELS[link.targetTicket?.status as TicketStatus] ?? link.targetTicket?.status ?? '—',
					assignee: personDisplayName(link.targetTicket?.assignee) || 'Unassigned',
					targetId: link.targetTicket?.id ?? '',
				})),
		[data.links]
	);

	const projectRows: ProjectLinkRow[] = useMemo(
		() =>
			data.links
				.filter(link => link.targetProject != null)
				.map(link => ({
					id: link.id,
					projectId: link.targetProject?.id ?? '—',
					name: link.targetProject?.name ?? '—',
					targetId: link.targetProject?.id ?? '',
				})),
		[data.links]
	);

	const totalCount = ticketRows.length + projectRows.length;
	const who = requesterName || 'this requester';

	// The action column reads `props.hidden` off every child, so a conditional `null`
	// among them throws — build the list and filter before passing it.
	const removeAction = canManage
		? [
				<Table.ActionColumn.Option
					key="remove"
					disabled={deleting}
					onClick={(row: {id: string}) => handleDelete(row.id)}
				>
					Remove link
				</Table.ActionColumn.Option>,
		  ]
		: [];

	if (totalCount === 0) {
		return (
			<EmptyState data-testid="related-empty-state">
				<EmptyBody>
					<Text color="auxiliary">No linked items yet</Text>
					<Detail color="medium">Link related tickets or projects to keep track of dependencies.</Detail>
					{onAddLink && (
						<Button emphasis={EMPHASIS.MEDIUM} icon="plus" onPress={onAddLink} data-cy="add-link-empty">
							Add link
						</Button>
					)}
				</EmptyBody>
			</EmptyState>
		);
	}

	return (
		<Stack data-testid="related-tab">
			<Header>
				<Intro>
					<SubHeading>{`Related items · ${who}`}</SubHeading>
					<Detail color="medium">
						{`${totalCount} ${totalCount === 1 ? 'item' : 'items'} associated with this requester. `}
						{`Other open tickets and projects tied to ${who}.`}
					</Detail>
				</Intro>
				{onAddLink && (
					<Button emphasis={EMPHASIS.MONO} icon="plus" onPress={onAddLink} data-cy="add-link">
						Add link
					</Button>
				)}
			</Header>

			{ticketRows.length > 0 && (
				<Group>
					<GroupHead>
						<Label>Tickets</Label>
						<Detail color="medium">{`(${ticketRows.length})`}</Detail>
					</GroupHead>
					<Table data={ticketRows} getRowId={row => row.id} width="100%" data-cy="related-tickets-table">
						<Table.TextColumn accessorKey="slug" header="Id" width="110px" />
						<Table.TextColumn accessorKey="subject" header="Subject" />
						<Table.TextColumn accessorKey="relationship" header="Relationship" width="140px" />
						<Table.TextColumn accessorKey="status" header="Status" width="160px" />
						<Table.AvatarColumn accessorKey="assignee" header="Assignee" width="190px" />
						<Table.ButtonColumn
							header="Open"
							hideHeader
							width="110px"
							button={(row: TicketLinkRow) => ({
								label: 'Open',
								onClick: () => navigate(`/tickets/${row.targetId}`),
							})}
						/>
						{removeAction.length > 0 ? (
							<Table.ActionColumn header="Actions" hideHeader>
								{removeAction}
							</Table.ActionColumn>
						) : undefined}
					</Table>
				</Group>
			)}

			{projectRows.length > 0 && (
				<Group>
					<GroupHead>
						<Label>Projects</Label>
						<Detail color="medium">{`(${projectRows.length})`}</Detail>
					</GroupHead>
					<Table data={projectRows} getRowId={row => row.id} width="100%" data-cy="related-projects-table">
						<Table.TextColumn accessorKey="projectId" header="Id" width="110px" />
						<Table.TextColumn accessorKey="name" header="Title" />
						{removeAction.length > 0 ? (
							<Table.ActionColumn header="Actions" hideHeader>
								{removeAction}
							</Table.ActionColumn>
						) : undefined}
					</Table>
				</Group>
			)}
		</Stack>
	);
}
